<template>
  <v-card outlined class="mt-2 primary--border">
    <v-card-title
      v-if="pricingTables.length"
      class="py-1 white--text primary text-center text-uppercase"
    >
      Event Charges
    </v-card-title>

    <v-card-text>
      <div v-for="table in pricingTables" :key="table.key">
        <!-- convention table -->
        <table
          class="pricing--table"
          v-if="!table.isStudent && table.categories.length"
        >
          <thead>
            <tr>
              <th class="pricing-label"></th>
              <th
                v-for="(category, idx) in table.categories"
                :key="`${table.key}-${category}-${idx}`"
                class="pricing-label text-right"
              >
                {{ category }}
              </th>
            </tr>
          </thead>
          <tbody
            v-for="(group, idx) in table.groups"
            :key="`member-pricing-${table.key}-${group.key}-${idx}`"
          >
            <tr>
              <th
                :colspan="table.categories.length + 1"
                class="text-left package-title"
              >
                {{ group.description }}
              </th>
            </tr>
            <tr v-if="hasEarlyBird(group) && hasEarlyBirdLCY(group)">
              <td class="price-label">
                Early Registration
                <span>
                  (before
                  {{
                    hasEarlyBird(group)
                      ? formatDate(
                          getPackage(group, table.categories[0])
                            ?.earlyBirdCutoffDate
                        )
                      : ""
                  }})
                </span>
              </td>
              <td
                v-for="(category, index) in table.categories"
                :key="`member-early-lcy-${group.key}-${category}-${index}`"
                class="font-weight-black text-right"
              >
                {{
                  formatPackagePrice(
                    getPackage(group, category),
                    "earlyBirdAmountInclLCY"
                  )
                }}
              </td>
            </tr>
            <tr v-if="hasNormalLCY(group)">
              <td class="price-label">
                <template v-if="hasEarlyBird(group)">
                  Late Registration
                  <span class="ml-1">
                    (after {{
                      formatDate(
                        getPackage(group, table.categories[0])
                          ?.earlyBirdCutoffDate
                      )
                    }})
                  </span></template
                >
                <template v-else> Charges</template>
              </td>
              <td
                v-for="(category, lidx) in table.categories"
                :key="`member-Late-Registration-lcy-${group.key}-${category}-${lidx}`"
                class="font-weight-black text-right"
              >
                {{
                  formatPackagePrice(
                    getPackage(group, category),
                    "amountInclLCY"
                  )
                }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th
                :colspan="table.categories.length + 1"
                class="text-left font-weight-light font-italic"
                v-if="hasEarlyRegistration(table)"
              >
                * By the date of registering for the Convention
              </th>
            </tr>
          </tfoot>
        </table>
        <!-- packages that apply to all categories -->
        <table
          v-if="!table.isStudent && hasAllCategoryPackages(table)"
          class="pricing--table mt-4"
        >
          <tbody
            v-for="(group, idx) in table.allCategoryGroups"
            :key="`all-category-${table.key}-${group.key}-${idx}`"
          >
            <tr>
              <th colspan="2" class="text-left package-title">
                {{ group.description }}
              </th>
            </tr>
            <tr v-if="hasEarlyBird(group) && hasEarlyBirdLCY(group)">
              <td class="price-label">
                Early Registration
                <span>
                  (before
                  {{ formatDate(group.packages[0]?.earlyBirdCutoffDate) }})
                </span>
              </td>
              <td
                :colspan="table.categories.length"
                class="font-weight-black text-right"
              >
                {{
                  formatPackagePrice(
                    group.packages[0],
                    "earlyBirdAmountInclLCY"
                  )
                }}
              </td>
            </tr>
            <tr v-if="hasNormalLCY(group)">
              <td class="price-label">
                <template v-if="hasEarlyBird(group)">
                  Late Registration
                  <span class="ml-1">
                    (after{{
                      formatDate(group.packages[0]?.earlyBirdCutoffDate)
                    }})
                  </span></template
                >
                <template v-else> Charges</template>
              </td>
              <td
                :colspan="table.categories.length"
                class="font-weight-black text-right"
              >
                {{ formatPackagePrice(group.packages[0], "amountInclLCY") }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- foreign table -->
        <table
          v-if="!table.isStudent && hasForeignPackages(table)"
          class="pricing--table"
        >
          <thead>
            <tr>
              <th class="pricing-title"></th>
              <template v-for="(group, gidx) in table.groups">
                <th
                  v-if="!isEmpty(group.categories)"
                  :key="`member-foreign-${table.key}-${group.key}-${gidx}`"
                  class="pricing-title text-right"
                >
                  {{ group.description }}
                </th>
              </template>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td class="pricing-label">International Delegate</td>
              <template v-for="group in table.groups">
                <td
                  class="price-label font-weight-black text-right"
                  v-if="!isEmpty(group.categories)"
                  :key="`international-delegate-foreign-${table.key}-${group.key}`"
                >
                  {{ group.packages[0]?.currencyCode }}
                  {{ group.packages[0]?.earlyBirdAmountInclVAT }}
                </td>
              </template>
            </tr>
          </tbody>
        </table>

        <!-- student table -->
        <table v-if="table.isStudent" class="pricing--table mt-6">
          <thead>
            <tr>
              <th class="pricing-label">Undergraduate Student</th>

              <th class="pricing-title text-right">Early Registration</th>
              <th class="pricing-title text-right">Late Registration</th>
            </tr>
          </thead>

          <tbody
            v-for="group in table.groups"
            :key="`student-foreign-${table.key}-${group.key}`"
          >
            <tr v-if="!isEmpty(group.categories)">
              <td class="pricing-label">
                {{ `(${group.description})` }}
              </td>

              <template
                v-for="category in table.categories"
                class="font-weight-black"
              >
                <td
                  class="font-weight-black text-right"
                  :key="`student-early-lcy-${group.key}-${category}`"
                  v-if="!isEmpty(group.categories)"
                >
                  {{
                    formatPackagePrice(
                      getPackage(group, category),
                      "earlyBirdAmountInclLCY"
                    )
                  }}
                </td>
              </template>
              <template
                v-for="category in table.categories"
                class="font-weight-black"
              >
                <td
                  class="font-weight-black text-right"
                  :key="`lcy-${group.key}-${category}`"
                  v-if="!isEmpty(group.categories)"
                >
                  {{
                    formatPackagePrice(
                      getPackage(group, category),
                      "amountInclLCY"
                    )
                  }}
                </td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  props: {
    filteredPackages: {
      type: Object,
      default: () => ({
        eventCosts: [],
      }),
    },
    checkGoodStanding: {
      type: Boolean,
    },
  },

  computed: {
    membershipCategories() {
      return this.$store.getters["Events/membershipCategories"];
    },

    eventPackageGroups() {
      const eventPackages = this.filteredPackages.eventCosts || [];

      const groups = {};

      eventPackages.forEach((eventPackage) => {
        const description = eventPackage.description?.trim() || "Other";

        if (!groups[description]) {
          groups[description] = {
            key: description,
            description,
            packages: [],
            allCategoryPackages: [],
            categories: [],
          };
        }

        // A blank appliesType means the package applies to ALL categories.
        const appliesType = eventPackage.appliesType?.trim() || "";

        if (!appliesType) {
          groups[description].allCategoryPackages.push(eventPackage);
        } else {
          groups[description].packages.push(eventPackage);
        }
      });

      Object.values(groups).forEach((group) => {
        const packages = group.packages;

        const hasPrice = (eventPackage) => {
          return (
            Number(eventPackage.amountInclLCY || 0) > 0 ||
            Number(eventPackage.amountIncludingVAT || 0) > 0 ||
            Number(eventPackage.earlyBirdAmountInclLCY || 0) > 0 ||
            Number(eventPackage.earlyBirdAmountInclVAT || 0) > 0
          );
        };

        const allPackagesAreZero =
          packages.length > 0 &&
          packages.every((eventPackage) => !hasPrice(eventPackage));

        if (packages.length === 1 || allPackagesAreZero) {
          group.packages = packages;
        } else {
          group.packages = packages.filter((eventPackage) =>
            hasPrice(eventPackage)
          );
        }

        group.categories = [];

        group.packages.forEach((eventPackage) => {
          const category = this.getPackageCategory(eventPackage);

          if (category && !group.categories.includes(category)) {
            group.categories.push(category);
          }
        });

        const nonMemberPackage = group.packages.find(
          (eventPackage) =>
            this.getPackageCategory(eventPackage) === "Non-Member"
        );

        if (nonMemberPackage && this.checkGoodStanding) {
          const iekMemberPackage = {
            ...nonMemberPackage,
            appliesType: "IEK Member not in Good Standing",
          };

          group.packages.push(iekMemberPackage);
          group.categories.push("IEK Member not in Good Standing *");
        }
      });

      return Object.values(groups).filter(
        (group) =>
          group.packages.length > 0 || group.allCategoryPackages.length > 0
      );
    },

    pricingTables() {
      const createTable = (isStudent) => {
        const groups = this.eventPackageGroups
          .map((group) => {
            const packages = group.packages.filter(
              (eventPackage) =>
                (this.getPackageCategory(eventPackage) === "Student") ===
                isStudent
            );
            const allCategoryPackages = group.allCategoryPackages.filter(
              (eventPackage) =>
                (this.getPackageCategory(eventPackage) === "Student") ===
                isStudent
            );
            return {
              ...group,
              packages,
              allCategoryPackages,
              categories: group.categories.filter((category) =>
                packages.some(
                  (eventPackage) =>
                    this.getPackageCategory(eventPackage) === category
                )
              ),
            };
          })
          .filter(
            (group) =>
              group.packages.length > 0 || group.allCategoryPackages.length > 0
          );
        const categories = [];
        const preferredOrder = [
          "IEK Member",
          "IEK Member in good standing",
          "IEK Member not in Good Standing *",
          "Non-Member",
        ];
        groups.forEach((group) => {
          group.categories.forEach((category) => {
            if (!categories.includes(category)) {
              categories.push(category);
            }
          });
        });
        return {
          key: isStudent ? "student" : "convention",
          isStudent,
          groups: groups.filter((group) => group.packages.length > 0),
          allCategoryGroups: groups
            .filter((group) => group.allCategoryPackages.length > 0)
            .map((group) => ({
              ...group,
              packages: group.allCategoryPackages,
            })),
          categories: [
            ...preferredOrder.filter((category) =>
              categories.includes(category)
            ),
            ...categories.filter(
              (category) => !preferredOrder.includes(category)
            ),
          ],
        };
      };
      return [createTable(false), createTable(true)].filter(
        (table) => table.groups.length > 0 || table.allCategoryGroups.length > 0
      );
    },
  },

  methods: {
    getPackageCategory(eventPackage) {
      const appliesType = eventPackage.appliesType?.trim() || "";

      const membershipCategory = eventPackage.membershipCategory?.trim() || "";

      if (!appliesType) {
        return " ";
      }

      if (
        appliesType.toLowerCase() === "member" &&
        membershipCategory.toUpperCase() === "STUDENT"
      ) {
        return "Student";
      }

      if (appliesType.toLowerCase() === "member") {
        if (this.checkGoodStanding) {
          return "IEK Member in good standing";
        }

        return "IEK Member";
      }

      if (appliesType.toLowerCase() === "iek member not in good standing") {
        return "IEK Member not in Good Standing *";
      }

      if (appliesType.toLowerCase() === "non-member") {
        return "Non-Member";
      }

      return appliesType;
    },

    getPackage(group, category) {
      return group.packages.find(
        (eventPackage) => this.getPackageCategory(eventPackage) === category
      );
    },

    hasEarlyBird(group) {
      return group.packages.some(
        (eventPackage) =>
          Number(eventPackage.earlyBirdAmountInclLCY || 0) > 0 ||
          Number(eventPackage.earlyBirdAmountInclVAT || 0) > 0
      );
    },

    hasEarlyBirdLCY(group) {
      return group.packages.some(
        (eventPackage) => Number(eventPackage.earlyBirdAmountInclLCY || 0) > 0
      );
    },

    hasAllCategoryPackages(table) {
      return (
        table.allCategoryGroups?.some(
          (group) =>
            group.allCategoryPackages?.length > 0 || group.packages?.length > 0
        ) ?? false
      );
    },

    hasEarlyBirdForeign(group) {
      return group.packages.some(
        (eventPackage) =>
          eventPackage.currencyCode !== "KES" &&
          Number(eventPackage.earlyBirdAmountInclVAT || 0) > 0
      );
    },

    hasNormal(group) {
      return group.packages.length > 0;
    },

    hasNormalLCY(group) {
      return group.packages.some(
        (eventPackage) => typeof eventPackage.amountInclLCY === "number"
      );
    },

    hasNormalForeign(group) {
      return group.packages.some(
        (eventPackage) =>
          eventPackage.currencyCode !== "KES" &&
          Number(eventPackage.amountIncludingVAT || 0) > 0
      );
    },

    formatPackagePrice(eventPackage, field) {
      if (!eventPackage) {
        return " ";
      }

      const value = Number(eventPackage[field] || 0);

      if (value === 0) {
        return "Free";
      }

      return `Kshs. ${this.formatCurrency(value)}`;
    },

    formatForeignPrice(eventPackage, field) {
      if (!eventPackage) {
        return " ";
      }

      if (eventPackage.currencyCode === "KES") {
        return "--";
      }

      const value = Number(eventPackage[field] || 0);

      if (value === 0) {
        return "0";
      }

      return `${eventPackage.currencyCode} ${this.formatCurrency(value)}`;
    },

    formatCurrency(value) {
      return this.$options.filters.currency(value);
    },

    formatDate(date) {
      if (!date) {
        return "--";
      }

      const parsedDate = new Date(date);

      if (Number.isNaN(parsedDate.getTime())) {
        return "--";
      }

      return parsedDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    getDescription(item) {
      const category = this.membershipCategories.find(
        (category) => category.Code === item
      );

      return category ? category.Description : "";
    },

    isEmpty(categories) {
      return !categories || categories.every((category) => !category.trim());
    },

    hasEarlyRegistration(table) {
      return table.groups?.some((group) =>
        group.packages?.some(
          (packageItem) =>
            packageItem.earlyBirdCutoffDate &&
            (packageItem.earlyBirdAmountExclVAT > 0 ||
              packageItem.earlyBirdAmountInclLCY > 0 ||
              packageItem.earlyBirdAmountInclVAT > 0)
        )
      );
    },

    hasForeignPackages(table) {
      return (
        table.groups?.some((group) =>
          group.packages?.some(
            (packageItem) =>
              packageItem.currencyCode?.trim().toUpperCase() !== "KES"
          )
        ) ?? false
      );
    },
  },
};
</script>

<style scoped>
.pricing--table {
  width: 100%;
  border-collapse: collapse !important;
}

.pricing--table th,
.pricing--table td {
  border: 1px solid #fff !important;
  padding: 8px;
}

.pricing--table th {
  font-weight: 600;
}

.pricing-label {
  width: 180px;
  color: orange !important;
}

.package-title {
  font-size: 15px;
  font-weight: 600;
  background: #f5f5f5;
}

.price-section {
  font-size: 14px;
}

.price-label {
  padding-left: 20px !important;
}

.price-label span {
  color: #878787;
}

.primary--border {
  border: 2px solid #9f232d;
}

td:first-child {
  color: #000;
  font-weight: bold;
}
</style>
